import React, { useState, useEffect } from 'react';
import { User } from '../../services/authService';
import { passwordService, PasswordEntry, CreatePasswordData } from '../../services/passwordService';
import './Password.css';

interface PasswordFormProps {
  user: User | null;
  entry?: PasswordEntry | null;
  onClose: () => void;
}

const PasswordForm: React.FC<PasswordFormProps> = ({ user, entry, onClose }) => {
  const [formData, setFormData] = useState<CreatePasswordData>({
    account_name: '',
    website_url: '',
    username: '',
    password: '',
    notes: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entry) {
      setFormData({
        account_name: entry.account_name || '',
        website_url: entry.website_url || '',
        username: entry.username || '',
        password: entry.password || '',
        notes: entry.notes || ''
      });
    }
  }, [entry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.account_name || !formData.password) {
      alert('Account name and password are required');
      return;
    }

    try {
      setLoading(true);
      
      if (entry) {
        await passwordService.updatePassword(entry.id, formData);
      } else {
        await passwordService.createPassword(formData);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving password:', error);
      alert('Failed to save password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreatePasswordData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="password-form-overlay" onClick={onClose}>
      <div className="password-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="password-form-header">
          <h2>{entry ? 'Edit Password' : 'Add Password'}</h2>
          <button className="password-form-close" onClick={onClose}>×</button>
        </div>
        
        <form className="password-form" onSubmit={handleSubmit}>
          <div className="password-form-group">
            <label>Account Name *</label>
            <input
              type="text"
              value={formData.account_name}
              onChange={(e) => handleChange('account_name', e.target.value)}
              required
              placeholder="e.g., Gmail, GitHub"
            />
          </div>

          <div className="password-form-group">
            <label>Website URL</label>
            <input
              type="url"
              value={formData.website_url}
              onChange={(e) => handleChange('website_url', e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="password-form-group">
            <label>Username / Email</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder="username or email"
            />
          </div>

          <div className="password-form-group">
            <label>Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                placeholder="Enter password"
                className="password-input-field"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="password-form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes"
              rows={3}
            />
          </div>

          <div className="password-form-actions">
            <button type="button" onClick={onClose} className="password-form-cancel">
              Cancel
            </button>
            <button type="submit" className="password-form-submit" disabled={loading}>
              {loading ? 'Saving...' : entry ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordForm;

